import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, ShoppingCart } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import './Chatbot.css';

/* ─── Static knowledge base (no API key needed) ─── */
const KB = {
  en: {
    greeting: "Hi! 👋 I'm **Zap**, TechStore's AI assistant. How can I help you today?",
    suggestions: ['🔍 Find a product', '🛒 My cart', '📦 Track order', '💰 Discounts', '📞 Contact us'],
  },
  fr: {
    greeting: "Bonjour! 👋 Je suis **Zap**, l'assistant IA de TechStore. Comment puis-je vous aider ?",
    suggestions: ['🔍 Trouver un produit', '🛒 Mon panier', '📦 Suivre commande', '💰 Remises', '📞 Nous contacter'],
  },
  ar: {
    greeting: "مرحباً! 👋 أنا **Zap**، مساعد TechStore الذكي. كيف يمكنني مساعدتك؟",
    suggestions: ['🔍 البحث عن منتج', '🛒 سلتي', '📦 تتبع الطلب', '💰 الخصومات', '📞 تواصل معنا'],
  },
};

/* ─── Rule-based response engine ─── */
const getAutoResponse = (input, lang, cartCount, navigate) => {
  const q = input.toLowerCase();
  const is = (...words) => words.some(w => q.includes(w));

  // ── Cart ──
  if (is('cart', 'panier', 'سلة')) {
    if (cartCount === 0) return { text: lang === 'fr' ? 'Votre panier est vide. Consultez nos produits !' : lang === 'ar' ? 'سلتك فارغة! تصفح منتجاتنا.' : 'Your cart is empty! Browse our products.', action: () => navigate('/products') };
    return { text: lang === 'fr' ? `Vous avez **${cartCount} article(s)** dans votre panier.` : lang === 'ar' ? `لديك **${cartCount} منتج** في سلتك.` : `You have **${cartCount} item(s)** in your cart.`, action: () => navigate('/cart') };
  }

  // ── Shipping ──
  if (is('shipping', 'livraison', 'شحن', 'deliver')) {
    return { text: lang === 'fr' ? '🚚 Livraison gratuite pour les commandes +500$. Sinon 15$. Délai : 2-3 jours ouvrables.' : lang === 'ar' ? '🚚 شحن مجاني للطلبات فوق 500$. وإلا 15$. المدة: 2-3 أيام عمل.' : '🚚 Free shipping on orders over $500. Otherwise $15. Delivery in 2-3 business days.' };
  }

  // ── Return / Refund ──
  if (is('return', 'refund', 'retour', 'remboursement', 'إرجاع', 'استرداد')) {
    return { text: lang === 'fr' ? '🔄 Vous avez **30 jours** pour retourner un produit. Contactez notre support pour initier le retour.' : lang === 'ar' ? '🔄 لديك **30 يومًا** لإرجاع المنتج. تواصل مع دعمنا للبدء.' : '🔄 You have **30 days** to return a product. Contact our support to initiate the return.', action: () => navigate('/contact') };
  }

  // ── Discount / Promo ──
  if (is('discount', 'promo', 'coupon', 'code', 'remise', 'خصم', 'كود')) {
    return { text: lang === 'fr' ? '🏷️ Codes promo disponibles :\n• **WELCOME10** — 10% de réduction\n• **TECH20** — 20% sur commandes +100$\n• **SAVE50** — 50$ de réduction sur commandes +300$' : lang === 'ar' ? '🏷️ أكواد الخصم المتاحة:\n• **WELCOME10** — خصم 10%\n• **TECH20** — خصم 20% للطلبات +100$\n• **SAVE50** — خصم 50$ للطلبات +300$' : '🏷️ Available discount codes:\n• **WELCOME10** — 10% off\n• **TECH20** — 20% on orders +$100\n• **SAVE50** — $50 off orders +$300' };
  }

  // ── Contact ──
  if (is('contact', 'support', 'help', 'aide', 'مساعدة', 'تواصل')) {
    return { text: lang === 'fr' ? '📞 Notre équipe est là pour vous aider !\n\n• 📧 support@techstore.com\n• 📱 +216 74 000 000\n• Lun-Sam, 9h-18h\n\nOu envoyez-nous un message directement.' : lang === 'ar' ? '📞 فريقنا هنا للمساعدة!\n\n• 📧 support@techstore.com\n• 📱 74 000 000\n• الاثنين-السبت، 9ص-6م' : '📞 Our team is here to help!\n\n• 📧 support@techstore.com\n• 📱 +216 74 000 000\n• Mon-Sat, 9am-6pm', action: () => navigate('/contact') };
  }

  // ── Laptop ──
  if (is('laptop', 'ordinateur', 'macbook', 'dell', 'xps', 'كمبيوتر', 'لابتوب')) {
    return { text: lang === 'fr' ? '💻 Nos laptops les plus populaires :\n• **MacBook Pro M3 Max** — 3 499$\n• **Dell XPS 15 OLED** — 2 299$\n\nVoulez-vous voir tous les laptops ?' : lang === 'ar' ? '💻 أشهر أجهزتنا المحمولة:\n• **MacBook Pro M3 Max** — 3,499$\n• **Dell XPS 15 OLED** — 2,299$' : '💻 Our most popular laptops:\n• **MacBook Pro M3 Max** — $3,499\n• **Dell XPS 15 OLED** — $2,299\n\nWant to see all laptops?', action: () => navigate('/products?category=Laptops') };
  }

  // ── Phone / Smartphone ──
  if (is('phone', 'smartphone', 'iphone', 'samsung', 'galaxy', 'هاتف', 'موبايل')) {
    return { text: lang === 'fr' ? '📱 Nos smartphones populaires :\n• **iPhone 15 Pro Max** — 1 199$\n• **Samsung Galaxy S24 Ultra** — 1 299$' : lang === 'ar' ? '📱 هواتفنا الذكية الأكثر مبيعًا:\n• **iPhone 15 Pro Max** — 1,199$\n• **Samsung Galaxy S24 Ultra** — 1,299$' : '📱 Our popular smartphones:\n• **iPhone 15 Pro Max** — $1,199\n• **Samsung Galaxy S24 Ultra** — $1,299', action: () => navigate('/products?category=Smartphones') };
  }

  // ── Audio / Headphones ──
  if (is('audio', 'headphone', 'casque', 'sony', 'écouteur', 'سماعة')) {
    return { text: lang === 'fr' ? '🎧 **Sony WH-1000XM5** — 349$\nMeilleure annulation de bruit du marché, 30h de batterie.' : lang === 'ar' ? '🎧 **Sony WH-1000XM5** — 349$\nأفضل إلغاء للضوضاء في السوق، 30 ساعة بطارية.' : '🎧 **Sony WH-1000XM5** — $349\nIndustry-leading noise canceling, 30h battery.', action: () => navigate('/products?category=Audio') };
  }

  // ── Warranty ──
  if (is('warranty', 'garantie', 'ضمان')) {
    return { text: lang === 'fr' ? '🛡️ Tous nos produits sont couverts par **1 an de garantie** fabricant. Certains produits Apple bénéficient de l\'AppleCare.' : lang === 'ar' ? '🛡️ جميع منتجاتنا مشمولة بـ **ضمان سنة** من الشركة المصنعة.' : '🛡️ All our products come with a **1-year manufacturer warranty**. Some Apple products include AppleCare.' };
  }

  // ── Payment ──
  if (is('payment', 'pay', 'paiement', 'payer', 'visa', 'stripe', 'دفع')) {
    return { text: lang === 'fr' ? '💳 Modes de paiement acceptés :\n• **Carte bancaire** (Visa, Mastercard via Stripe)\n• **Paiement à la livraison** (espèces)' : lang === 'ar' ? '💳 طرق الدفع المقبولة:\n• **بطاقة بنكية** (Visa, Mastercard عبر Stripe)\n• **الدفع عند الاستلام**' : '💳 Accepted payment methods:\n• **Credit/Debit card** (Visa, Mastercard via Stripe)\n• **Cash on delivery**' };
  }

  // ── Order tracking ──
  if (is('order', 'track', 'commande', 'suivi', 'طلب', 'تتبع')) {
    return { text: lang === 'fr' ? '📦 Pour suivre votre commande, rendez-vous dans **Mes Commandes** depuis votre profil.' : lang === 'ar' ? '📦 لتتبع طلبك، اذهب إلى **طلباتي** من ملفك الشخصي.' : '📦 To track your order, go to **My Orders** from your profile.', action: () => navigate('/orders') };
  }

  // ── Hello / Hi ──
  if (is('hello', 'hi', 'hey', 'bonjour', 'salut', 'bonsoir', 'مرحبا', 'السلام', 'اهلا')) {
    return { text: lang === 'fr' ? 'Bonjour ! 😊 Comment puis-je vous aider aujourd\'hui ?' : lang === 'ar' ? 'أهلاً وسهلاً! 😊 كيف يمكنني مساعدتك اليوم؟' : 'Hello! 😊 How can I help you today?' };
  }

  // ── Thank you ──
  if (is('thank', 'merci', 'شكر', 'شكرا')) {
    return { text: lang === 'fr' ? 'Avec plaisir ! 😊 N\'hésitez pas si vous avez d\'autres questions.' : lang === 'ar' ? 'على الرحب والسعة! 😊 لا تتردد في السؤال.' : "You're welcome! 😊 Feel free to ask if you have more questions." };
  }

  // ── Default ──
  return {
    text: lang === 'fr'
      ? "Je ne suis pas sûr de comprendre. Voici ce que je peux faire :\n\n• 🔍 Rechercher des produits\n• 📦 Infos livraison & retour\n• 💰 Codes de réduction\n• 📞 Contacter le support\n\nPosez votre question différemment ou contactez notre équipe !"
      : lang === 'ar'
      ? "لم أفهم جيدًا. إليك ما يمكنني فعله:\n\n• 🔍 البحث عن منتجات\n• 📦 معلومات الشحن والإرجاع\n• 💰 أكواد الخصم\n• 📞 التواصل مع الدعم"
      : "I'm not sure I understood. Here's what I can help with:\n\n• 🔍 Find products\n• 📦 Shipping & return info\n• 💰 Discount codes\n• 📞 Contact support\n\nTry rephrasing or contact our team!",
  };
};

/* ─── Markdown-lite renderer ─── */
function renderText(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

/* ─── Timestamp ─── */
function getTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ─── Main Chatbot Component ─── */
export default function Chatbot() {
  const { lang } = useSettings();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(1);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Init greeting when first opened
  useEffect(() => {
    if (open && !hasOpened) {
      setHasOpened(true);
      setUnread(0);
      const greeting = KB[lang]?.greeting || KB.en.greeting;
      setMessages([{ id: 1, role: 'bot', text: greeting, time: getTime() }]);
    }
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, hasOpened, lang]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content) return;
    setInput('');

    const userMsg = { id: Date.now(), role: 'user', text: content, time: getTime() };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);

    // Simulate thinking delay (600–1200ms)
    const delay = 600 + Math.random() * 600;
    await new Promise(r => setTimeout(r, delay));

    const response = getAutoResponse(content, lang, cartCount, navigate);
    setTyping(false);

    const botMsg = { id: Date.now() + 1, role: 'bot', text: response.text, time: getTime(), action: response.action, actionLabel: response.actionLabel };
    setMessages(prev => [...prev, botMsg]);
  }, [input, lang, cartCount, navigate]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleSuggestion = (s) => {
    // Strip emoji prefix
    const clean = s.replace(/^[\p{Emoji}\s]+/u, '').trim();
    sendMessage(clean);
  };

  const suggestions = KB[lang]?.suggestions || KB.en.suggestions;

  return (
    <>
      {/* FAB button */}
      <button className="chatbot-fab" onClick={() => setOpen(o => !o)} aria-label="Open chat">
        {open
          ? <X size={24} color="white" />
          : <MessageCircle size={26} color="white" fill="white" />
        }
        {!open && unread > 0 && <span className="chatbot-badge">{unread}</span>}
      </button>

      {/* Chat window */}
      {open && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-avatar">⚡</div>
            <div className="chatbot-header-info">
              <div className="chatbot-header-name">Zap — TechStore AI</div>
              <div className="chatbot-header-status">
                <span className="chatbot-status-dot" />
                {lang === 'ar' ? 'متصل الآن' : lang === 'fr' ? 'En ligne maintenant' : 'Online now'}
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setOpen(false)}>
              <X size={16} />
            </button>
          </div>

          {/* Quick suggestions */}
          <div className="chatbot-suggestions">
            {suggestions.map(s => (
              <button key={s} className="chatbot-suggestion-btn" onClick={() => handleSuggestion(s)}>{s}</button>
            ))}
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`chatbot-msg ${msg.role}`}>
                <div className="chatbot-msg-avatar">
                  {msg.role === 'bot' ? '⚡' : '👤'}
                </div>
                <div>
                  <div
                    className="chatbot-bubble"
                    dangerouslySetInnerHTML={{ __html: renderText(msg.text) }}
                  />
                  {msg.action && (
                    <button
                      onClick={msg.action}
                      style={{ marginTop: 8, padding: '6px 14px', borderRadius: 10, background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 6, transition: 'var(--transition)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}>
                      <ShoppingCart size={13} />
                      {msg.actionLabel || (lang === 'ar' ? 'اذهب' : lang === 'fr' ? 'Voir' : 'Go')} →
                    </button>
                  )}
                  <div className="chatbot-time">{msg.time}</div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div className="chatbot-msg bot">
                <div className="chatbot-msg-avatar">⚡</div>
                <div>
                  <div className="chatbot-bubble">
                    <div className="chatbot-typing">
                      <span /><span /><span />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={lang === 'ar' ? 'اكتب رسالتك...' : lang === 'fr' ? 'Écrivez votre message...' : 'Type a message...'}
            />
            <button
              className="chatbot-send"
              onClick={() => sendMessage()}
              disabled={!input.trim() || typing}
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
